import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { canAccess } from '../../rbac';
import { certificateAPI } from '../../services/api';
import GovernmentCertificateCard from '../../components/public/GovernmentCertificateCard';
import { printCertificateElement } from '../../utils/certificatePdf';

export default function CertificateViewPage({ embedded = false, tenantSlug: tenantSlugProp, certId: certIdProp, onClose }) {
  const routeParams = useParams();
  const tenantSlug = tenantSlugProp || routeParams.tenantSlug;
  const certId = certIdProp || routeParams.certId;
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const printRef = useRef();

  const isStaff = canAccess(user?.role, 'certificates', user);

  useEffect(() => {
    if (!tenantSlug || !certId) return;
    loadCertificate();
  }, [tenantSlug, certId]); // eslint-disable-line

  const loadCertificate = async () => {
    try {
      const res = await certificateAPI.getCertificateView(tenantSlug, certId);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'प्रमाणपत्र लोड करण्यात अयशस्वी');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const certNo = data?.certificate?.certificate_number || data?.certificate?.application_uid || certId;
      const fileName = `Certificate_${certNo}.pdf`;
      const response = await certificateAPI.downloadPdf(tenantSlug, certId);
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('PDF डाऊनलोड करताना त्रुटी आली. कृपया नंतर पुन्हा प्रयत्न करा किंवा प्रिंट पर्याय वापरा.');
    } finally {
      setDownloading(false);
    }
  };

  const handlePrint = () => {
    if (printRef.current) {
      printCertificateElement(printRef.current, `प्रमाणपत्र #${certId}`);
    } else {
      window.print();
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>⏳ प्रमाणपत्र लोड होत आहे...</div>;
  if (error) {
    return (
      <div style={{ maxWidth: 640, margin: '60px auto', background: '#fff', borderRadius: 16, padding: '40px 24px', textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
        <div style={{ fontSize: '3rem', marginBottom: 12 }}>⚠️</div>
        <h2 style={{ color: '#d97706', margin: '0 0 10px', fontSize: '1.4rem' }}>प्रमाणपत्र अजून जारी झालेले नाही</h2>
        <p style={{ color: '#64748b', fontSize: '0.92rem', lineHeight: 1.6, marginBottom: 24 }}>
          {error}<br />
          तुमचा अर्ज मंजूर झाला असल्यास ग्रामपंचायत कर्मचाऱ्यांकडून <strong>प्रमाणपत्र अधिकृतरीत्या जारी (Issued)</strong> करण्याची वाट पहा.
        </p>
        {embedded ? (
          <button type="button" onClick={onClose} style={{ padding: '10px 24px', background: '#1a237e', color: '#fff', border: 0, borderRadius: 8, fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer' }}>
            ← अर्ज तपशीलाकडे परत जा
          </button>
        ) : (
          <Link to={`/gp/${tenantSlug}/dashboard`} style={{ padding: '10px 24px', background: '#1a237e', color: '#fff', borderRadius: 8, textDecoration: 'none', fontWeight: 700, fontSize: '0.9rem' }}>
            ← डॅशबोर्डवर परत जा
          </Link>
        )}
      </div>
    );
  }

  if (!data) return null;

  const { certificate, issuance, rendered_certificate: renderedCertificate } = data;
  const isIssued = certificate?.status === 'issued';

  // Citizen protection: Block printable card if status is not 'issued'
  if (!isIssued && !isStaff) {
    return (
      <div style={{ maxWidth: 640, margin: '60px auto', background: '#fff', borderRadius: 16, padding: '40px 24px', textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
        <div style={{ fontSize: '3.5rem', marginBottom: 12 }}>⏳</div>
        <h2 style={{ color: '#1a237e', margin: '0 0 10px', fontSize: '1.4rem' }}>प्रमाणपत्र जारी होण्याची प्रतीक्षा आहे</h2>
        <p style={{ color: '#64748b', fontSize: '0.92rem', lineHeight: 1.6, marginBottom: 24 }}>
          तुमचा अर्ज मंजूर झाला आहे. परंतु अधिकृत डिजिटल स्वाक्षरीयुक्त प्रमाणपत्र अजून ग्रामपंचायतीकडून <strong>जारी (Issued)</strong> झालेले नाही.<br />
          ग्रामसेवकांनी प्रमाणपत्र जारी केल्यानंतर येथे तुम्हाला अधिकृत दाखला डाऊनलोड करता येईल.
        </p>
        {embedded ? (
          <button type="button" onClick={onClose} style={{ padding: '10px 24px', background: '#1a237e', color: '#fff', border: 0, borderRadius: 8, fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer' }}>
            ← अर्ज तपशीलाकडे परत जा
          </button>
        ) : (
          <Link to={`/gp/${tenantSlug}/dashboard`} style={{ padding: '10px 24px', background: '#1a237e', color: '#fff', borderRadius: 8, textDecoration: 'none', fontWeight: 700, fontSize: '0.9rem' }}>
            ← माझ्या खात्याकडे परत जा
          </Link>
        )}
      </div>
    );
  }

  return (
    <div className={`cert-page-wrapper${embedded ? ' cert-page-embedded' : ''}`} style={{ background: embedded ? '#f1f5f9' : '#e2e8f0', minHeight: embedded ? 'auto' : '100vh', padding: '20px 10px', borderRadius: embedded ? 14 : 0 }}>
      {/* Non-issued Staff Alert Banner */}
      {!isIssued && isStaff && (
        <div className="no-print" style={{ maxWidth: 820, margin: '0 auto 14px', background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 10, padding: '10px 16px', color: '#92400e', fontWeight: 700, fontSize: '0.86rem' }}>
          ⚠️ अ‍ॅडमिन टीप: हे प्रमाणपत्र अजून <strong>"issued" (जारी)</strong> स्थितीमध्ये नाही. नागरिक सध्या हे प्रमाणपत्र डाऊनलोड करू शकत नाहीत.
        </div>
      )}

      {/* Toolbar */}
      <div className="no-print" style={{ maxWidth: 820, margin: '0 auto 16px', display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        {embedded ? (
          <button type="button" onClick={onClose} style={{ background: '#fff', color: '#1a237e', border: '1.5px solid #cbd5e1', padding: '9px 18px', borderRadius: 8, fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer' }}>
            ← अर्ज तपशीलाकडे परत
          </button>
        ) : (
          <Link to={`/gp/${tenantSlug}/dashboard`} style={{ background: '#fff', color: '#1a237e', border: '1.5px solid #cbd5e1', padding: '9px 18px', borderRadius: 8, fontSize: '0.9rem', fontWeight: 700, textDecoration: 'none' }}>
            ← परत डॅशबोर्डकडे
          </Link>
        )}

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {isIssued && (
            <button
              onClick={handleDownloadPDF}
              disabled={downloading}
              style={{
                background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)',
                color: '#fff',
                border: 'none',
                padding: '10px 20px',
                borderRadius: 8,
                fontSize: '0.92rem',
                fontWeight: 800,
                cursor: downloading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                boxShadow: '0 4px 12px rgba(46, 125, 50, 0.3)',
                opacity: downloading ? 0.8 : 1,
              }}
            >
              {downloading ? '⏳ PDF डाऊनलोड होत आहे...' : '⬇️ PDF डाऊनलोड करा'}
            </button>
          )}

          <button
            onClick={handlePrint}
            style={{
              background: '#fff',
              color: '#1a237e',
              border: '1.5px solid #1a237e',
              padding: '10px 18px',
              borderRadius: 8,
              fontSize: '0.92rem',
              fontWeight: 800,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            🖨️ प्रिंट करा
          </button>
        </div>
      </div>

      {/* ── REAL GOVERNMENT OF MAHARASHTRA CERTIFICATE CARD ── */}
      <div className="cert-print-container" ref={printRef}>
        <GovernmentCertificateCard
          templateSnapshot={renderedCertificate?.template_snapshot}
          dataSnapshot={renderedCertificate?.data_snapshot}
          issuance={issuance}
        />
      </div>

      {/* Robust Print styles to fix blank pages and guarantee 1-page A4 print */}
      <style>{`
        @media print {
          /* Hide portal headers, footers, navigation, buttons, and utility elements */
          body > header, body > footer,
          .public-header, .public-footer, .public-nav, .header-utility, .header-main,
          nav, aside, .no-print, .cert-top-bar, .cert-action-bar,
          .gov-marquee-container, .citizen-dashboard-header, .citizen-tabs, .social-row {
            display: none !important;
          }

          /* Reset all top-level layout containers to block layout without flex or padding */
          html, body, #root, .public-layout, .public-main, .public-container, .cert-page-wrapper {
            margin: 0 !important;
            padding: 0 !important;
            background: #ffffff !important;
            box-shadow: none !important;
            border: none !important;
            width: 100% !important;
            max-width: 100% !important;
            min-height: auto !important;
            height: auto !important;
            display: block !important;
            overflow: visible !important;
            float: none !important;
            position: static !important;
          }

          /* Ensure Government Certificate Card fits on a single A4 page without 2nd page overflow */
          .cert-print-container {
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 auto !important;
            padding: 0 !important;
            box-shadow: none !important;
            page-break-inside: avoid !important;
            page-break-after: avoid !important;
            page-break-before: avoid !important;
          }

          .gov-cert-frame {
            width: 100% !important;
            max-width: 100% !important;
            min-height: 285mm !important;
            max-height: 297mm !important;
            margin: 0 auto !important;
            padding: 8mm var(--certificate-horizontal-padding, 10mm) 5mm !important;
            box-sizing: border-box !important;
            box-shadow: none !important;
            background: #ffffff !important;
            border: var(--certificate-border-width, 2px) solid var(--certificate-border, var(--certificate-accent, #1a237e)) !important;
            page-break-inside: avoid !important;
            page-break-after: avoid !important;
            page-break-before: avoid !important;
            break-inside: avoid !important;
            display: flex !important;
            flex-direction: column !important;
            justify-content: space-between !important;
          }

          .gov-cert-header,
          .gov-cert-content,
          .gov-cert-footer-container {
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
            background: #ffffff !important;
          }

          .gov-cert-footer {
            display: flex !important;
            visibility: visible !important;
            justify-content: space-between !important;
          }

          .gov-cert-brand-row {
            height: 82px !important;
            display: flex !important;
            justify-content: center !important;
            align-items: center !important;
          }

          .gov-cert-emblem {
            width: 58px !important;
            height: 80px !important;
            max-width: 58px !important;
            max-height: 80px !important;
            object-fit: contain !important;
            display: block !important;
            margin: 0 auto !important;
          }

          .gov-cert-free-canvas {
            background: transparent !important;
            background-image: none !important;
          }

          @page {
            size: A4 portrait;
            margin: 3mm 4mm 3mm 4mm !important;
          }
        }
      `}</style>
    </div>
  );
}
